import { useState, useEffect } from 'react';

interface ExhaustionDetail {
  tokensUsed?: number;
  cap?: number;
  resetsAt?: string;
  upgradeUrl?: string;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

/**
 * Full-screen overlay shown when a session start is blocked due to
 * TOKEN_ALLOWANCE_EXHAUSTED (402). Listens for the `token-exhaustion`
 * custom event dispatched by apiFetch.
 */
export function TokenExhaustionOverlay() {
  const [detail, setDetail] = useState<ExhaustionDetail | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      setDetail((e as CustomEvent<ExhaustionDetail>).detail);
    };
    window.addEventListener('token-exhaustion', handler);
    return () => window.removeEventListener('token-exhaustion', handler);
  }, []);

  if (!detail) return null;

  const resetLabel = detail.resetsAt ? formatDate(detail.resetsAt) : 'next month';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="token-exhaustion-title"
      data-testid="token-exhaustion-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0,0,0,0.6)',
        backdropFilter: 'blur(4px)',
      }}
    >
      <div
        style={{
          maxWidth: 480,
          width: '90%',
          padding: 32,
          borderRadius: 16,
          backgroundColor: 'var(--card-bg)',
          border: '1px solid var(--border)',
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 48, marginBottom: 16 }}>&#x26A0;</div>
        <h2
          id="token-exhaustion-title"
          style={{
            fontSize: 22,
            fontWeight: 700,
            color: 'var(--text)',
            margin: '0 0 12px',
            fontFamily: 'var(--font-heading)',
          }}
        >
          Monthly token allowance reached
        </h2>
        <p
          style={{
            fontSize: 15,
            color: 'var(--text-muted)',
            margin: '0 0 8px',
            lineHeight: 1.6,
          }}
        >
          You have used all {(detail.cap ?? 1_000_000).toLocaleString()} tokens for this month.
          Your allowance resets on {resetLabel}.
        </p>
        <p
          style={{
            fontSize: 14,
            color: 'var(--text-muted)',
            margin: '0 0 24px',
          }}
        >
          Upgrade to Starter for 5M tokens/seat.
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
          <a
            href={detail.upgradeUrl ?? '/upgrade'}
            style={{
              display: 'inline-block',
              padding: '12px 24px',
              borderRadius: 8,
              backgroundColor: 'var(--primary)',
              color: '#fff',
              fontSize: 14,
              fontWeight: 600,
              textDecoration: 'none',
              fontFamily: 'var(--font-body)',
            }}
          >
            Upgrade to Starter &rarr;
          </a>
          <button
            onClick={() => setDetail(null)}
            style={{
              padding: '12px 24px',
              borderRadius: 8,
              border: '1px solid var(--border)',
              backgroundColor: 'transparent',
              color: 'var(--text-muted)',
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer',
              fontFamily: 'var(--font-body)',
            }}
          >
            Wait until {resetLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
